import { PlayerState, EnemyData, StatusState, StatusType } from '../types';
import { FEATURES } from '../features';

type StatusHolder = PlayerState | EnemyData;

function ensureStatuses(holder: StatusHolder): StatusState[] {
  if (!FEATURES.statusSystem) return [];
  holder.statuses = holder.statuses ?? [];
  return holder.statuses;
}

export function getStatus(holder: StatusHolder, type: StatusType): StatusState | undefined {
  return ensureStatuses(holder).find(status => status.type === type);
}

export function getStatusStacks(holder: StatusHolder, type: StatusType): number {
  return getStatus(holder, type)?.stacks ?? 0;
}

export function hasStatus(holder: StatusHolder, type: StatusType): boolean {
  return getStatusStacks(holder, type) > 0;
}

export function applyStatus(
  holder: StatusHolder,
  type: StatusType,
  stacks: number,
  duration: number,
): void {
  if (!FEATURES.statusSystem) return;
  if (stacks <= 0 || duration <= 0) return;

  const statuses = ensureStatuses(holder);
  const existing = statuses.find(status => status.type === type);
  if (existing) {
    existing.stacks += stacks;
    existing.duration = Math.max(existing.duration, duration);
    return;
  }

  statuses.push({ type, stacks, duration });
}

export function setStatus(
  holder: StatusHolder,
  type: StatusType,
  stacks: number,
  duration: number,
): void {
  if (!FEATURES.statusSystem) return;
  const statuses = ensureStatuses(holder);
  const existing = statuses.find(status => status.type === type);
  if (existing) {
    existing.stacks = Math.max(0, stacks);
    existing.duration = Math.max(0, duration);
  } else if (stacks > 0 && duration > 0) {
    statuses.push({ type, stacks, duration });
  }
  cleanupStatuses(holder);
}

export function reduceStatus(holder: StatusHolder, type: StatusType, stacks: number): void {
  if (!FEATURES.statusSystem || stacks <= 0) return;
  const status = getStatus(holder, type);
  if (!status) return;
  status.stacks = Math.max(0, status.stacks - stacks);
  cleanupStatuses(holder);
}

export function tickStatusesStartTurn(holder: StatusHolder): void {
  if (!FEATURES.statusSystem) return;
  const statuses = ensureStatuses(holder);
  for (const status of statuses) {
    status.duration -= 1;
  }
  cleanupStatuses(holder);
}

export function listStatuses(holder: StatusHolder): StatusState[] {
  return [...ensureStatuses(holder)];
}

export function cleanupStatuses(holder: StatusHolder): void {
  if (!FEATURES.statusSystem) return;
  holder.statuses = ensureStatuses(holder)
    .filter(status => status.stacks > 0 && status.duration > 0);
}

export function getIncomingDamageMultiplierForPlayer(player: PlayerState): number {
  if (!FEATURES.statusSystem) return 1;
  const exposed = getStatusStacks(player, 'exposed');
  return exposed > 0 ? 1.25 : 1;
}

export function getDefenseMultiplierForPlayer(player: PlayerState): number {
  if (!FEATURES.statusSystem) return 1;
  const frail = getStatusStacks(player, 'frail');
  return frail > 0 ? 0.75 : 1;
}

export function getIncomingDamageMultiplierForEnemy(enemy: EnemyData): number {
  if (!FEATURES.statusSystem) return 1;
  const brittle = getStatusStacks(enemy, 'brittle');
  return brittle > 0 ? 1.25 : 1;
}

export function getEnemyIntentReduction(enemy: EnemyData): number {
  if (!FEATURES.statusSystem) return 0;
  return getStatusStacks(enemy, 'weakened') * 3;
}

export function consumeEnemyWardedReduction(enemy: EnemyData): number {
  if (!FEATURES.statusSystem) return 0;
  const warded = getStatus(enemy, 'warded');
  if (!warded || warded.stacks <= 0) return 0;
  const reduction = warded.stacks * 4;
  warded.stacks = 0;
  cleanupStatuses(enemy);
  return reduction;
}

export function consumePlayerJammed(player: PlayerState): boolean {
  if (!FEATURES.statusSystem) return false;
  const jammed = getStatus(player, 'jammed');
  if (!jammed || jammed.stacks <= 0) return false;
  jammed.stacks -= 1;
  cleanupStatuses(player);
  return true;
}
